"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Check, ChevronsUpDown, Loader2, Plus, Save, Trash2 } from "lucide-react"
import { HubCard, ToolHeader, ToolbarPill } from "@/components/hub/hub-canvas"
import { toast } from "sonner"
import { useMappingConfig } from "@/hooks/use-mapping-config"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { useTargetMapping } from "@/hooks/use-target-mapping"
import { useAtsSource } from "@/contexts/ats-source-context"
import { AtsFieldPathPicker } from "@/components/shared/ats-field-path-picker"
import { cn } from "@/lib/utils"
import type { AtsDestinationFieldInfo } from "@/types/fbo-api"
import type {
  FboTargetColumnMapping,
  FboTargetMappingConfiguration,
  FboTargetRowCondition,
  FboTargetSheetMapping,
  RowConditionOperator,
  TargetSheetSpec,
} from "@/types/target-mapping"

const TARGET_NAME = "OnboardingImport"
const NONE_VALUE = "__none__"

interface SelectOption {
  value: string
  label: string
}

interface AtsEntityOption {
  name: string
  entityType: string
}

// ATS entities are per (mapping type, ATS source); cache key combines both
const atsEntitiesKey = (mappingName: string, sourceCode: string | null) => `${mappingName}::${sourceCode ?? ""}`

/**
 * Editor for the FBO -> target translation layer. Source values come from FBO entity
 * fields (the same catalog ATS mappings target) or, per column, directly from a raw
 * ATS field of the sheet's source mapping entities. Defaults are pre-populated until
 * the tenant saves an override.
 */
export function TargetMappingEditor() {
  const { t, n } = useTranslation()
  const { loadTargetMapping, loadTargetSpec, loadTargetSourceFields, saveTargetMapping } = useTargetMapping()
  const { loadMappingNames, loadConfig } = useMappingConfig()
  // ATS columns read raw fields from the selected source's schema; the field picker
  // (via useTenantAtsSystem) already resolves to this source, so columns are authored
  // against it and stamped with it on edit.
  const { selectedSource } = useAtsSource()
  const selectedSourceCode = selectedSource?.code ?? null

  const [config, setConfig] = useState<FboTargetMappingConfiguration | null>(null)
  const [spec, setSpec] = useState<TargetSheetSpec[]>([])
  const [mappingNames, setMappingNames] = useState<string[]>([])
  const [sourceFieldsByMapping, setSourceFieldsByMapping] = useState<Record<string, AtsDestinationFieldInfo[]>>({})
  const [atsEntitiesByMapping, setAtsEntitiesByMapping] = useState<Record<string, AtsEntityOption[]>>({})
  const [activeSheet, setActiveSheet] = useState<string>("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([loadTargetMapping(TARGET_NAME), loadTargetSpec(TARGET_NAME), loadMappingNames()])
      .then(([loadedConfig, loadedSpec, names]) => {
        // Ensure every spec sheet has a section so it's editable
        const sheets = loadedSpec.map(
          (sheetSpec) =>
            loadedConfig.sheets?.find((s) => s.sheetName === sheetSpec.sheetName) ?? {
              sheetName: sheetSpec.sheetName,
              sourceMappingName: sheetSpec.defaultSourceMappingName,
              columnMappings: [],
            }
        )
        setConfig({ ...loadedConfig, targetName: TARGET_NAME, sheets })
        setSpec(loadedSpec)
        setMappingNames(names)
        setActiveSheet(loadedSpec[0]?.sheetName ?? "")
      })
      .catch((err) => setError(err instanceof Error ? err.message : t("targetMapping.messages.loadFailed")))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Lazily load FBO source fields and ATS entity names per mapping type used by sheets
  useEffect(() => {
    if (!config) return
    const needed = Array.from(
      new Set(config.sheets.map((s) => s.sourceMappingName).filter((n): n is string => !!n))
    )

    needed
      .filter((name) => !sourceFieldsByMapping[name])
      .forEach((name) => {
        loadTargetSourceFields(name)
          .then((fields) => setSourceFieldsByMapping((current) => ({ ...current, [name]: fields })))
          .catch(() => undefined)
      })

    // Entities are defined in the per-source ATS mapping config, so cache them per
    // (mapping, selected source) and reload when the source switches
    needed
      .filter((name) => !atsEntitiesByMapping[atsEntitiesKey(name, selectedSourceCode)])
      .forEach((name) => {
        loadConfig(name, selectedSourceCode)
          .then((mappingConfig) =>
            setAtsEntitiesByMapping((current) => ({
              ...current,
              [atsEntitiesKey(name, selectedSourceCode)]: (mappingConfig.entities ?? []).map((e) => ({ name: e.name, entityType: e.entityType })),
            }))
          )
          .catch(() => setAtsEntitiesByMapping((current) => ({ ...current, [atsEntitiesKey(name, selectedSourceCode)]: [] })))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.sheets.map((s) => s.sourceMappingName).join("|"), selectedSourceCode])

  const updateSheet = useCallback((sheetName: string, update: Partial<FboTargetSheetMapping>) => {
    setConfig((current) =>
      current
        ? {
            ...current,
            sheets: current.sheets.map((sheet) =>
              sheet.sheetName === sheetName ? { ...sheet, ...update } : sheet
            ),
          }
        : current
    )
  }, [])

  const handleSave = async () => {
    if (!config) return
    setIsSaving(true)
    setError(null)
    try {
      const success = await saveTargetMapping(TARGET_NAME, config)
      if (success) {
        toast.success(t("targetMapping.messages.saved"))
      } else {
        setError(t("targetMapping.messages.saveFailed"))
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> {t("targetMapping.state.loading")}
      </div>
    )
  }

  return (
    <div className="p-4">
      <ToolHeader
        title={t("targetMapping.header.title")}
        description={t("targetMapping.header.description")}
        actions={
          <ToolbarPill variant="primary" onClick={handleSave} disabled={isSaving || !config} icon={isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}>
            {t("common.actions.save")}
          </ToolbarPill>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {config && (
        <HubCard className="flex min-h-[520px]">
          <aside className="w-[240px] shrink-0 border-r border-card-border p-3">
            <div className="th-section-label mb-2 px-1">{t("targetMapping.sheets.label", { count: n(config.sheets.length) })}</div>
            <ul className="space-y-0.5">
              {config.sheets.map((sheet) => {
                const active = sheet.sheetName === activeSheet
                const mapped = sheet.columnMappings?.length ?? 0
                return (
                  <li key={sheet.sheetName}>
                    <button
                      type="button"
                      onClick={() => setActiveSheet(sheet.sheetName)}
                      className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[13px] transition-colors ${active ? "bg-accent font-semibold text-accent-foreground" : "hover:bg-row-hover"}`}
                    >
                      <span className="truncate">{sheet.sheetName}</span>
                      <span className={`ml-2 rounded-pill px-1.5 py-0.5 text-[10px] font-bold ${mapped > 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{mapped}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </aside>
          <div className="min-w-0 flex-1">
            {config.sheets
              .filter((sheet) => sheet.sheetName === activeSheet)
              .map((sheet) => (
                <SheetEditor
                  key={sheet.sheetName}
                  sheet={sheet}
                  sheetSpec={spec.find((s) => s.sheetName === sheet.sheetName)!}
                  mappingNames={mappingNames}
                  sourceFields={sheet.sourceMappingName ? sourceFieldsByMapping[sheet.sourceMappingName] ?? [] : []}
                  atsEntities={sheet.sourceMappingName ? atsEntitiesByMapping[atsEntitiesKey(sheet.sourceMappingName, selectedSourceCode)] ?? [] : []}
                  selectedSourceCode={selectedSourceCode}
                  onChange={updateSheet}
                />
              ))}
          </div>
        </HubCard>
      )}
    </div>
  )
}

interface SheetEditorProps {
  sheet: FboTargetSheetMapping
  sheetSpec: TargetSheetSpec
  mappingNames: string[]
  sourceFields: AtsDestinationFieldInfo[]
  atsEntities: AtsEntityOption[]
  selectedSourceCode: string | null
  onChange: (sheetName: string, update: Partial<FboTargetSheetMapping>) => void
}

function SheetEditor({ sheet, sheetSpec, mappingNames, sourceFields, atsEntities, selectedSourceCode, onChange }: SheetEditorProps) {
  const { t, n } = useTranslation()
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const previousCount = React.useRef(sheet.columnMappings?.length ?? 0)

  // Scroll a newly added row into view (rows are appended at the bottom)
  useEffect(() => {
    const count = sheet.columnMappings?.length ?? 0
    if (count > previousCount.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
    previousCount.current = count
  }, [sheet.columnMappings?.length])

  const mappedColumns = useMemo(
    () => new Set(sheet.columnMappings?.map((c) => c.targetColumn) ?? []),
    [sheet.columnMappings]
  )

  // Options memoized so memoized rows only re-render when the catalogs actually change
  const sourceFieldOptions = useMemo<SelectOption[]>(
    () => sourceFields.map((f) => ({ value: f.FieldPath, label: f.FieldPath })),
    [sourceFields]
  )
  const columnOptions = useMemo<SelectOption[]>(
    () => sheetSpec.columns.map((c) => ({ value: c, label: c })),
    [sheetSpec.columns]
  )

  // The FBO columns are a single shared set shown in every context; ATS columns are
  // shown only for the selected source. Other sources' ATS columns stay in the config
  // (preserved on save) but are hidden here — switch the source to edit them. Original
  // indices are kept so edits target the right entry in the full list.
  const visibleColumns = useMemo(
    () =>
      (sheet.columnMappings ?? [])
        .map((mapping, index) => ({ mapping, index }))
        .filter(
          ({ mapping }) =>
            mapping.sourceType !== "Ats" ||
            !mapping.sourceAtsSource ||
            mapping.sourceAtsSource.toLowerCase() === (selectedSourceCode ?? "").toLowerCase()
        ),
    [sheet.columnMappings, selectedSourceCode]
  )

  const hiddenAtsColumnCount = (sheet.columnMappings?.length ?? 0) - visibleColumns.length

  const updateColumnMapping = useCallback(
    (index: number, update: Partial<FboTargetColumnMapping>) => {
      const next = [...(sheet.columnMappings ?? [])]
      next[index] = { ...next[index], ...update }
      onChange(sheet.sheetName, { columnMappings: next })
    },
    [sheet.columnMappings, sheet.sheetName, onChange]
  )

  const removeColumnMapping = useCallback(
    (index: number) => {
      onChange(sheet.sheetName, { columnMappings: (sheet.columnMappings ?? []).filter((_, i) => i !== index) })
    },
    [sheet.columnMappings, sheet.sheetName, onChange]
  )

  const addColumnMapping = () => {
    const firstUnmapped = sheetSpec.columns.find((c) => !mappedColumns.has(c)) ?? sheetSpec.columns[0]
    onChange(sheet.sheetName, {
      columnMappings: [
        ...(sheet.columnMappings ?? []),
        { sourceType: "Fbo", sourceFboField: null, targetColumn: firstUnmapped, defaultValue: null },
      ],
    })
  }

  return (
    <div>
      <div className="border-b border-card-border px-5 py-4">
        <h2 className="font-display text-base font-bold">{sheet.sheetName}</h2>
        <p className="text-xs text-muted-foreground">
          {t("targetMapping.sheet.columnsSummary", { available: n(sheetSpec.columns.length), mapped: n(sheet.columnMappings?.length ?? 0) })}
        </p>
        <div className="flex items-center gap-2 pt-2">
          <Label className="text-xs whitespace-nowrap">{t("targetMapping.sheet.fboSource")}</Label>
          <Select
            value={sheet.sourceMappingName ?? NONE_VALUE}
            onValueChange={(value) => onChange(sheet.sheetName, { sourceMappingName: value === NONE_VALUE ? null : value })}
          >
            <SelectTrigger className="w-56 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>{t("targetMapping.sheet.notGenerated")}</SelectItem>
              {mappingNames.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="p-5">
        {sheet.sourceMappingName ? (
          <>
            <RowConditionsEditor
              conditions={sheet.rowConditions ?? []}
              sourceFieldOptions={sourceFieldOptions}
              onChange={(rowConditions) => onChange(sheet.sheetName, { rowConditions })}
            />

            {/* Native overflow: Radix ScrollArea viewports don't scroll inside max-height-only containers */}
            <div ref={scrollRef} className="max-h-[28rem] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">{t("targetMapping.columns.source")}</TableHead>
                    <TableHead className="w-80">{t("targetMapping.columns.sourceField")}</TableHead>
                    <TableHead className="w-72">{t("targetMapping.columns.targetColumn")}</TableHead>
                    <TableHead>{t("targetMapping.columns.defaultValue")}</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleColumns.map(({ mapping, index }) => (
                    <MappingRow
                      key={index}
                      index={index}
                      mapping={mapping}
                      sourceFieldOptions={sourceFieldOptions}
                      columnOptions={columnOptions}
                      atsEntities={atsEntities}
                      sourceMappingName={sheet.sourceMappingName!}
                      selectedSourceCode={selectedSourceCode}
                      onUpdate={updateColumnMapping}
                      onRemove={removeColumnMapping}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={addColumnMapping}>
                <Plus className="h-4 w-4 mr-1" /> {t("targetMapping.columns.add")}
              </Button>
              {hiddenAtsColumnCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t((hiddenAtsColumnCount === 1 ? "targetMapping.columns.hiddenOne" : "targetMapping.columns.hiddenOther"), { count: n(hiddenAtsColumnCount) })}
                </span>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("targetMapping.sheet.notGeneratedHelp")}</p>
        )}
      </div>
    </div>
  )
}

const ROW_CONDITION_OPERATORS: { value: RowConditionOperator; labelKey: TranslationKey }[] = [
  { value: "eq", labelKey: "targetMapping.operators.eq" },
  { value: "ne", labelKey: "targetMapping.operators.ne" },
  { value: "empty", labelKey: "targetMapping.operators.empty" },
  { value: "notEmpty", labelKey: "targetMapping.operators.notEmpty" },
]

interface RowConditionsEditorProps {
  conditions: FboTargetRowCondition[]
  sourceFieldOptions: SelectOption[]
  onChange: (conditions: FboTargetRowCondition[]) => void
}

/**
 * Row-eligibility conditions for a sheet: pulled records whose FBO row fails any
 * condition are excluded from the export (and counted in the export progress).
 */
function RowConditionsEditor({ conditions, sourceFieldOptions, onChange }: RowConditionsEditorProps) {
  const { t } = useTranslation()
  const updateCondition = (index: number, update: Partial<FboTargetRowCondition>) => {
    const next = [...conditions]
    next[index] = { ...next[index], ...update }
    onChange(next)
  }

  return (
    <div className="border rounded-md p-3 mb-3 bg-muted/30">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-medium">{t("targetMapping.conditions.title")}</p>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onChange([...conditions, { sourceFboField: "", operator: "notEmpty", value: null }])}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> {t("targetMapping.conditions.add")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-2">{t("targetMapping.conditions.help")}</p>

      {conditions.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">{t("targetMapping.conditions.none")}</p>
      ) : (
        <div className="space-y-2">
          {conditions.map((condition, index) => {
            const needsValue = condition.operator === "eq" || condition.operator === "ne"
            return (
              <div key={index} className="flex flex-wrap gap-2 items-center">
                <div className="w-72">
                  <SearchableSelect
                    options={sourceFieldOptions}
                    value={condition.sourceFboField || null}
                    placeholder={t("targetMapping.conditions.fieldPlaceholder")}
                    onValueChange={(value) => value && updateCondition(index, { sourceFboField: value })}
                  />
                </div>
                <Select
                  value={condition.operator}
                  onValueChange={(operator) => updateCondition(index, { operator: operator as RowConditionOperator })}
                >
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROW_CONDITION_OPERATORS.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        {t(op.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {needsValue && (
                  <Input
                    className="w-44 h-8 text-xs"
                    placeholder={t("targetMapping.conditions.valuePlaceholder")}
                    value={condition.value ?? ""}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                  />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onChange(conditions.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface MappingRowProps {
  index: number
  mapping: FboTargetColumnMapping
  sourceFieldOptions: SelectOption[]
  columnOptions: SelectOption[]
  atsEntities: AtsEntityOption[]
  sourceMappingName: string
  selectedSourceCode: string | null
  onUpdate: (index: number, update: Partial<FboTargetColumnMapping>) => void
  onRemove: (index: number) => void
}

/**
 * Memoized so editing one row (notably typing in the default-value input) does not
 * re-render every other row — sheets carry dozens of rows with large option lists.
 */
const MappingRow = React.memo(function MappingRow({
  index,
  mapping,
  sourceFieldOptions,
  columnOptions,
  atsEntities,
  sourceMappingName,
  selectedSourceCode,
  onUpdate,
  onRemove,
}: MappingRowProps) {
  const { t } = useTranslation()
  const isAtsSource = mapping.sourceType === "Ats"
  // Rows are filtered to the selected source, so an ATS column shown here belongs to it;
  // author actions stamp it with that source.
  const columnSource = mapping.sourceAtsSource ?? selectedSourceCode

  return (
    <TableRow>
      <TableCell>
        <Select
          value={isAtsSource ? "Ats" : "Fbo"}
          onValueChange={(value) =>
            onUpdate(index, value === "Ats"
              ? { sourceType: "Ats", sourceFboField: null, sourceAtsSource: selectedSourceCode }
              : { sourceType: "Fbo", sourceAtsEntityName: null, sourceAtsField: null, sourceAtsSource: null })
          }
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Fbo">FBO</SelectItem>
            <SelectItem value="Ats">ATS</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        {isAtsSource ? (
          atsEntities.length === 0 ? (
            // Nothing to pull from: the ATS mapping this sheet sources from has no entities yet
            <p className="text-xs text-muted-foreground py-1.5">
              {t("targetMapping.row.noEntitiesPrefix")}
              <span className="font-medium">{sourceMappingName}</span>
              {t("targetMapping.row.noEntitiesSuffix")}
            </p>
          ) : (
            <div className="space-y-1">
              <div className="flex gap-1">
                <Select
                  value={mapping.sourceAtsEntityName ?? atsEntities[0].name}
                  onValueChange={(value) => onUpdate(index, { sourceAtsEntityName: value, sourceAtsSource: selectedSourceCode })}
                >
                  <SelectTrigger className="h-8 text-xs w-32 shrink-0">
                    <SelectValue placeholder={t("targetMapping.row.entityPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {atsEntities.map((entity) => (
                      <SelectItem key={entity.name} value={entity.name}>
                        {entity.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <AtsFieldPathPicker
                  entityType={
                    (atsEntities.find((e) => e.name === (mapping.sourceAtsEntityName ?? atsEntities[0].name)) ?? atsEntities[0])
                      .entityType
                  }
                  value={mapping.sourceAtsField ?? null}
                  onChange={(value) => onUpdate(index, { sourceAtsField: value, sourceAtsSource: selectedSourceCode })}
                />
              </div>
              {columnSource && (
                <p className="text-[10px] text-muted-foreground">{t("targetMapping.row.atsSource", { code: columnSource })}</p>
              )}
            </div>
          )
        ) : (
          <SearchableSelect
            options={sourceFieldOptions}
            value={mapping.sourceFboField}
            placeholder={t("targetMapping.row.constant")}
            allowClear
            onValueChange={(value) => onUpdate(index, { sourceFboField: value })}
          />
        )}
      </TableCell>
      <TableCell>
        <SearchableSelect
          options={columnOptions}
          value={mapping.targetColumn}
          placeholder={t("targetMapping.row.selectColumn")}
          onValueChange={(value) => value && onUpdate(index, { targetColumn: value })}
        />
      </TableCell>
      <TableCell>
        <Input
          className="h-8 text-xs"
          value={mapping.defaultValue == null ? "" : String(mapping.defaultValue)}
          placeholder={t("targetMapping.row.defaultPlaceholder")}
          onChange={(e) => onUpdate(index, { defaultValue: e.target.value === "" ? null : e.target.value })}
        />
      </TableCell>
      <TableCell>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onRemove(index)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  )
})

interface SearchableSelectProps {
  options: SelectOption[]
  value: string | null
  placeholder: string
  allowClear?: boolean
  onValueChange: (value: string | null) => void
}

/**
 * Popover + Command picker for large option lists (hundreds of FBO fields / sheet
 * columns): options are only rendered when the popover opens, and the list is
 * searchable — both of which plain Selects lack at this scale.
 */
function SearchableSelect({ options, value, placeholder, allowClear, onValueChange }: SearchableSelectProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="h-8 w-full justify-between text-xs font-normal">
          <span className={cn("truncate", !value && "text-muted-foreground")}>{value ?? placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder={t("targetMapping.select.search")} className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>{t("targetMapping.select.noMatch")}</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onValueChange(null)
                    setOpen(false)
                  }}
                  className="text-xs text-muted-foreground"
                >
                  {t("targetMapping.row.constant")}
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    // Capture via closure — cmdk normalizes its callback argument
                    onValueChange(option.value)
                    setOpen(false)
                  }}
                  className="text-xs"
                >
                  <Check className={cn("mr-2 h-3 w-3", value === option.value ? "opacity-100" : "opacity-0")} />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
